import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2, Plus, Check, RotateCcw, Undo2, Lock, Eye, EyeOff } from "lucide-react";
import { api } from "../lib/api";
import Tip from "./Tip";

/**
 * Segretaria IA · FAB + drawer chat + tool-call action cards.
 * Mount once in <Shell> for authenticated users only.
 * Phase 6: inline L2 password prompt, new action types, multi-action.
 */
export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingActions, setPendingActions] = useState({}); // action_id → action
  const listRef = useRef(null);
  const convIdRef = useRef(null);  // to avoid state-propagation races in send()

  const loadConversations = async () => {
    try { setConversations(await api.chatbotListConversations()); } catch {}
  };

  const openChat = async () => {
    setOpen(true);
    if (!convIdRef.current) {
      await loadConversations();
      const list = await api.chatbotListConversations();
      if (list.length) {
        convIdRef.current = list[0].id;
        setConvId(list[0].id);
        await loadMessages(list[0].id);
      } else {
        await newConversation();
      }
    }
  };

  const newConversation = async () => {
    try {
      const c = await api.chatbotCreateConversation();
      convIdRef.current = c.id;
      setConvId(c.id);
      setMessages([]);
      setPendingActions({});
      await loadConversations();
      return c.id;
    } catch (e) {
      toast.error("Impossibile creare conversazione");
      return null;
    }
  };

  const loadMessages = async (id) => {
    if (!id) return;
    try {
      const msgs = await api.chatbotListMessages(id);
      setMessages(msgs);
    } catch {}
  };

  const switchConversation = async (id) => {
    convIdRef.current = id;
    setConvId(id);
    setPendingActions({});
    await loadMessages(id);
  };

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  // Ascolta evento globale per apertura dalla bottom tab bar mobile.
  useEffect(() => {
    const handler = () => { openChat(); };
    window.addEventListener("pxos:open-chatbot", handler);
    return () => window.removeEventListener("pxos:open-chatbot", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    let cid = convIdRef.current || convId;
    if (!cid) {
      cid = await newConversation();
      if (!cid) return;
    }
    setSending(true);
    setInput("");
    const tempUserId = `tmp-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempUserId, role: "user", content: text, created_at: new Date().toISOString() }]);
    try {
      const resp = await api.chatbotSendMessage(cid, text);
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempUserId);
        return [...filtered, resp.user_message, resp.assistant_message];
      });
      if (resp.action) {
        setPendingActions((p) => ({ ...p, [resp.action.id]: resp.action }));
      }
      loadConversations();
    } catch (e) {
      toast.error(errMsg(e, "Errore invio messaggio"));
      setMessages((prev) => prev.filter((m) => m.id !== tempUserId));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const confirmAction = async (actionId, stepupToken = null) => {
    try {
      const res = await api.chatbotConfirmAction(actionId, stepupToken);
      const action = pendingActions[actionId];
      const kind = action?.action_type;
      const isEmail = kind === "send_accountant_email";
      const canUndo = res.can_undo !== false && !isEmail;
      const successLabel =
        isEmail ? "✓ Email al commercialista inviata"
        : kind === "create_financial_item" ? "✓ Voce finanziaria creata"
        : kind === "create_cycle" ? "✓ Ciclo creato"
        : kind === "create_reminder" ? "✓ Promemoria creato"
        : kind === "multi_action" ? `✓ ${res.count || ""} cicli creati`
        : kind === "move_cycle" ? "✓ Ciclo spostato"
        : kind === "delete_cycle" ? "✓ Ciclo eliminato"
        : kind === "delete_financial_item" ? "✓ Voce finanziaria eliminata"
        : kind === "mark_financial_paid" ? "✓ Segnata come pagata"
        : kind === "add_memory" ? "✓ Memoria aggiunta"
        : kind === "link_document_finance" ? "✓ Documento collegato"
        : "✓ Azione eseguita";
      toast.success(successLabel, canUndo ? {
        action: {
          label: "Annulla",
          onClick: async () => {
            try { await api.chatbotUndoAction(actionId); toast.success("Annullato"); }
            catch (e) { toast.error(errMsg(e, "Impossibile annullare")); }
          },
        },
        duration: 60000,
      } : { duration: 4000 });
      setPendingActions((p) => {
        const next = { ...p };
        next[actionId] = { ...next[actionId], status: "executed", resulting_resource_id: res.resource_id };
        return next;
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 && detail === "stepup_required") {
        toast.error("Password richiesta per confermare l'azione");
        throw e;
      }
      toast.error(errMsg(e, "Errore conferma"));
      throw e;
    }
  };

  const rejectAction = async (actionId) => {
    try {
      await api.chatbotRejectAction(actionId);
      toast("Proposta annullata");
      setPendingActions((p) => ({ ...p, [actionId]: { ...p[actionId], status: "rejected" } }));
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  return (
    <>
      {!open && (
        <Tip label="Apri Segretaria IA" side="left">
          <button
            onClick={openChat}
            className="fixed right-5 z-40 rounded-full shadow-lg pxos-btn-primary flex items-center gap-2 px-4 py-3 bottom-20 md:bottom-5"
            data-testid="chatbot-fab"
            aria-label="Apri Segretaria IA"
          >
            <Sparkles size={18}/> <span className="hidden sm:inline text-sm">Segretaria IA</span>
          </button>
        </Tip>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end"
             data-testid="chatbot-drawer" role="dialog" aria-modal="true" aria-labelledby="chatbot-title">
          <div className="absolute inset-0 bg-black/20" onClick={() => setOpen(false)} data-testid="chatbot-backdrop"/>
          <aside className="relative w-full sm:w-[480px] h-full bg-white shadow-2xl flex flex-col" style={{ background: "var(--pxos-surface)" }}>
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--pxos-border)" }}>
              <div className="flex items-center gap-2">
                <Sparkles size={18} style={{ color: "var(--pxos-accent)" }}/>
                <div>
                  <div id="chatbot-title" className="font-display text-lg font-semibold">Segretaria IA</div>
                  <div className="text-[10px] uppercase tracking-widest opacity-60">assistente</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Tip label="Inizia una nuova conversazione">
                  <button onClick={newConversation} className="p-1.5 hover:bg-black/5 rounded-full" data-testid="chatbot-new-conv" aria-label="Nuova conversazione"><Plus size={16}/></button>
                </Tip>
                <Tip label="Chiudi la Segretaria IA">
                  <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-black/5 rounded-full" data-testid="chatbot-close" aria-label="Chiudi"><X size={18}/></button>
                </Tip>
              </div>
            </div>

            {/* Conversation switcher (compact) */}
            {conversations.length > 1 && (
              <div className="px-3 py-2 border-b overflow-x-auto flex gap-1.5" style={{ borderColor: "var(--pxos-border)" }} data-testid="chatbot-conv-list">
                {conversations.slice(0, 8).map((c) => (
                  <button key={c.id}
                    onClick={() => switchConversation(c.id)}
                    className="text-[11px] whitespace-nowrap rounded-full border px-2.5 py-1"
                    style={{
                      background: c.id === convId ? "var(--pxos-primary-light)" : "white",
                      fontWeight: c.id === convId ? 600 : 400,
                      borderColor: c.id === convId ? "var(--pxos-accent)" : "var(--pxos-border)",
                    }}
                    title={`Conversazione: ${c.title}`}
                    data-testid="chatbot-conv-item">
                    {c.title.slice(0, 30)}
                  </button>
                ))}
              </div>
            )}

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
              {messages.length === 0 && !sending && (
                <div className="text-center opacity-60 text-sm py-12" data-testid="chatbot-empty">
                  <Sparkles size={32} className="mx-auto mb-2 opacity-40"/>
                  <div className="font-medium">Ciao! Sono la tua Segretaria IA.</div>
                  <div className="text-xs mt-2">Prova: "Cosa devo fare oggi?" o "Aggiungi la scadenza fattura ENEL per il 15 marzo, ricorda 7 giorni prima".</div>
                </div>
              )}
              {messages.map((m) => (
                <ChatMessage
                  key={m.id}
                  msg={m}
                  action={m.action_id ? pendingActions[m.action_id] : null}
                  onConfirm={confirmAction}
                  onReject={rejectAction}
                />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-xs opacity-60" data-testid="chatbot-typing">
                  <Loader2 size={14} className="animate-spin"/> Sto pensando...
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="p-3 border-t flex items-end gap-2" style={{ borderColor: "var(--pxos-border)" }}>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrivi in italiano naturale..."
                className="pxos-input flex-1 resize-none"
                rows={2}
                maxLength={4000}
                disabled={sending}
                data-testid="chatbot-input"
                aria-label="Messaggio"
              />
              <Tip label="Invia messaggio (Invio)">
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  className="pxos-btn-primary p-2.5"
                  data-testid="chatbot-send"
                  aria-label="Invia">
                  {sending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
                </button>
              </Tip>
            </div>
            <div className="text-[10px] opacity-50 px-4 pb-2">
              La conferma di ogni azione è sempre richiesta prima dell'esecuzione. Rate limit: 30 msg/min.
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

// ─── Sub-components ────────────────────────────────────────

function ChatMessage({ msg, action, onConfirm, onReject }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid="chatbot-msg-user">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm px-3 py-2 text-sm whitespace-pre-wrap"
             style={{ background: "var(--pxos-primary-light)", color: "var(--pxos-text-dark)" }}>
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start" data-testid="chatbot-msg-assistant">
      <div className="max-w-[92%] space-y-2">
        <div className="rounded-2xl rounded-tl-sm px-3 py-2 text-sm bg-white border whitespace-pre-wrap"
             style={{ borderColor: "var(--pxos-border)", color: "var(--pxos-text-dark)" }}>
          <FormattedText text={msg.content}/>
        </div>
        {action && <ActionCard action={action} onConfirm={onConfirm} onReject={onReject}/>}
      </div>
    </div>
  );
}

function FormattedText({ text }) {
  // Very lightweight: bold **x**, keep newlines
  const parts = (text || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^\*\*(.+)\*\*$/);
        return m ? <strong key={i}>{m[1]}</strong> : <span key={i}>{p}</span>;
      })}
    </>
  );
}

function ActionCard({ action, onConfirm, onReject }) {
  const p = action.parameters || {};
  const status = action.status;
  const isProposed = status === "proposed";
  const isExecuted = status === "executed";
  const isRejected = status === "rejected";
  const kind = action.action_type;
  const needsStepup = !!action.requires_stepup;

  // Common inline confirm handler — shows password prompt if needed
  const [showPwdPrompt, setShowPwdPrompt] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleConfirmClick = async () => {
    if (needsStepup) {
      setShowPwdPrompt(true);
      return;
    }
    try { await onConfirm(action.id); } catch {}
  };

  const submitStepup = async () => {
    if (!pwd || pwdBusy) return;
    setPwdBusy(true);
    try {
      const tok = await api.stepupPassword(pwd);
      await onConfirm(action.id, tok.stepup_token);
      setShowPwdPrompt(false);
      setPwd("");
    } catch (e) {
      // Keep the prompt open. Do NOT trigger the global 401→refresh→logout path
      // (already excluded by the interceptor for /auth/stepup/).
      const status = e?.response?.status;
      const msg = status === 429 ? "Troppi tentativi. Riprova tra 15 minuti."
                 : status === 401 ? "Password errata. Riprova."
                 : errMsg(e, "Errore verifica password");
      toast.error(msg);
    } finally {
      setPwdBusy(false);
    }
  };

  // Finance action rendering (create_financial_item)
  if (kind === "create_financial_item") {
    const typeLabels = {
      income_expected: "Entrata prevista", income_received: "Entrata ricevuta",
      fixed_monthly: "Costo fisso mensile", fixed_annual: "Costo fisso annuale",
      tax: "Tassa", contribution: "Contributo", subscription: "Abbonamento",
      credit_card_payment: "Pagamento carta di credito", pac: "PAC",
      planned_purchase: "Acquisto pianificato", one_off: "Una tantum",
      renewal: "Rinnovo", reserve_fund: "Fondo di riserva", variable_expense: "Spesa variabile",
    };
    const missingAmount = p.amount_expected == null;
    const missingDate = !p.due_date;
    return (
      <div className="pxos-card p-3 border-2" style={{ borderColor: "#5B21B6" }}
           data-testid="chatbot-action-card-finance" data-action-status={status}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-1.5">
          <span>{isExecuted ? "✓ Confermato" : isRejected ? "✕ Annullato" : "Voce finanziaria proposta"}</span>
          <span>·</span>
          <span>{typeLabels[p.item_type] || p.item_type || "?"}</span>
        </div>
        <div className="text-sm font-medium mb-2">{p.name || "(senza nome)"}</div>
        <div className="text-xs space-y-1">
          <div className={missingAmount ? "font-semibold" : ""} style={{ color: missingAmount ? "#B42318" : "inherit" }} data-testid="fin-amount-line">
            Importo previsto: {missingAmount ? <b>⚠️ mancante</b> : <b>{fmtEur(p.amount_expected)}</b>}
          </div>
          <div className={missingDate ? "font-semibold" : ""} style={{ color: missingDate ? "#8A5A00" : "inherit" }} data-testid="fin-date-line">
            Scadenza: {missingDate ? <b>⚠️ mancante</b> : <b>{fmtDate(p.due_date)}</b>}
          </div>
          {p.frequency && p.frequency !== "one_off" && <div>Frequenza: <b>{FREQ_IT_LABELS[p.frequency] || p.frequency}</b></div>}
          {p.provider_name && <div>Fornitore: <b>{p.provider_name}</b></div>}
          {p.entity_type && p.entity_type !== "personal" && <div>Entità: <b>{p.entity_type}</b></div>}
        </div>
        {(missingAmount || missingDate) && isProposed && (
          <div className="text-[11px] mt-2 opacity-80 pxos-card p-2" style={{ background: "#FFF8E1", color: "#8A5A00" }}>
            Puoi confermare comunque (i dati mancanti resteranno vuoti e potrai completarli in seguito),
            oppure annullare e ridettarmeli.
          </div>
        )}
        {isProposed && !showPwdPrompt && (
          <div className="flex items-center gap-2 mt-3">
            <Tip label={missingAmount || missingDate ? "Crea la voce con i dati parziali" : "Conferma e crea la voce"}>
              <button onClick={handleConfirmClick}
                      className="pxos-btn-primary text-xs" data-testid="chatbot-action-confirm">
                <Check size={12}/> Conferma
              </button>
            </Tip>
            <Tip label="Non creare, ignora questa proposta">
              <button onClick={() => onReject(action.id)}
                      className="text-xs underline opacity-70" data-testid="chatbot-action-reject">
                Annulla
              </button>
            </Tip>
          </div>
        )}
        {isProposed && showPwdPrompt && (
          <InlineStepupPrompt pwd={pwd} setPwd={setPwd} showPwd={showPwd} setShowPwd={setShowPwd}
                              pwdBusy={pwdBusy} submitStepup={submitStepup}
                              onCancel={() => { setShowPwdPrompt(false); setPwd(""); }} />
        )}
        {isExecuted && (
          <div className="text-xs opacity-70 mt-2 flex items-center gap-1">
            <Check size={12} className="text-green-700"/> Voce finanziaria creata · <span className="opacity-70">annulla nel toast per 60s</span>
          </div>
        )}
      </div>
    );
  }

  // Multi-action rendering
  if (kind === "multi_action") {
    const cycles = action.sub_actions || p.cycles || [];
    return (
      <div className="pxos-card p-3 border-2" style={{ borderColor: "var(--pxos-accent)" }}
           data-testid="chatbot-action-card-multi" data-action-status={status}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-1.5">
          <span>{isExecuted ? "✓ Batch eseguito" : isRejected ? "✕ Batch annullato" : "Batch proposto"}</span>
          <span>·</span>
          <span>{cycles.length} cicli</span>
        </div>
        <ol className="text-xs space-y-1 list-decimal ml-5">
          {cycles.map((c, i) => (
            <li key={i} data-testid={`multi-cycle-${i}`}>
              <b>{c.title || "(senza titolo)"}</b>
              {c.scheduled_date || c.due_date ? <> · {fmtDate(c.scheduled_date || c.due_date)}</> : null}
              {c.scheduled_time ? <> · {c.scheduled_time}</> : null}
            </li>
          ))}
        </ol>
        {isProposed && (
          <div className="flex items-center gap-2 mt-3">
            <Tip label="Conferma tutti i cicli">
              <button onClick={handleConfirmClick}
                      className="pxos-btn-primary text-xs" data-testid="chatbot-action-confirm">
                <Check size={12}/> Conferma batch
              </button>
            </Tip>
            <Tip label="Annulla batch">
              <button onClick={() => onReject(action.id)}
                      className="text-xs underline opacity-70" data-testid="chatbot-action-reject">
                Annulla
              </button>
            </Tip>
          </div>
        )}
      </div>
    );
  }

  // Sensitive actions (with L2)
  if (["move_cycle", "delete_cycle", "delete_financial_item", "mark_financial_paid", "send_accountant_email"].includes(kind)) {
    const kindLabel = {
      move_cycle: "Spostamento ciclo", delete_cycle: "Eliminazione ciclo",
      delete_financial_item: "Eliminazione voce finanziaria",
      mark_financial_paid: "Segna come pagata",
      send_accountant_email: "Invio al commercialista",
    }[kind];
    const summaryLines = [];
    if (kind === "move_cycle") {
      summaryLines.push(<div key="t">Ciclo: <b>{p._task_title || "?"}</b></div>);
      summaryLines.push(<div key="d">Nuova data: <b>{fmtDate(p.new_date)}</b>{p.new_time ? ` · ${p.new_time}` : ""}</div>);
    } else if (kind === "delete_cycle") {
      summaryLines.push(<div key="t">Ciclo da eliminare: <b>{p._task_title || "?"}</b></div>);
    } else if (kind === "delete_financial_item") {
      summaryLines.push(<div key="t">Voce da eliminare: <b>{p._item_name || "?"}</b></div>);
    } else if (kind === "mark_financial_paid") {
      summaryLines.push(<div key="t">Voce: <b>{p._item_name || "?"}</b></div>);
      summaryLines.push(<div key="a">Importo pagato: <b>{fmtEur(p.amount_actual)}</b></div>);
      if (p.paid_date) summaryLines.push(<div key="d">Data: <b>{fmtDate(p.paid_date)}</b></div>);
    } else if (kind === "send_accountant_email") {
      summaryLines.push(<div key="fy">Anno fiscale: <b>{p.fiscal_year}</b></div>);
      if (p.period_hint) summaryLines.push(<div key="p">Periodo: {p.period_hint}</div>);
    }
    return (
      <div className="pxos-card p-3 border-2" style={{ borderColor: "#B45309" }}
           data-testid={`chatbot-action-card-${kind}`} data-action-status={status}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-1.5">
          <Lock size={10}/>
          <span>{isExecuted ? "✓ Eseguita" : isRejected ? "✕ Annullata" : "Richiede password"}</span>
          <span>·</span>
          <span>{kindLabel}</span>
        </div>
        <div className="text-xs space-y-1 mb-2">{summaryLines}</div>
        {isProposed && !showPwdPrompt && (
          <div className="flex items-center gap-2 mt-3">
            <Tip label="Conferma con password">
              <button onClick={handleConfirmClick}
                      className="pxos-btn-primary text-xs" data-testid="chatbot-action-confirm">
                <Lock size={12}/> Conferma con password
              </button>
            </Tip>
            <Tip label="Non eseguire">
              <button onClick={() => onReject(action.id)}
                      className="text-xs underline opacity-70" data-testid="chatbot-action-reject">
                Annulla
              </button>
            </Tip>
          </div>
        )}
        {isProposed && showPwdPrompt && (
          <InlineStepupPrompt pwd={pwd} setPwd={setPwd} showPwd={showPwd} setShowPwd={setShowPwd}
                              pwdBusy={pwdBusy} submitStepup={submitStepup}
                              onCancel={() => { setShowPwdPrompt(false); setPwd(""); }} />
        )}
        {isExecuted && (
          <div className="text-xs opacity-70 mt-2 flex items-center gap-1">
            <Check size={12} className="text-green-700"/> Azione eseguita
          </div>
        )}
      </div>
    );
  }

  // add_memory / link_document_finance / create_reminder → simple confirm
  if (["add_memory", "link_document_finance", "create_reminder"].includes(kind)) {
    const kindLabel = {
      add_memory: "Memoria Segretaria",
      link_document_finance: "Collega documento → voce",
      create_reminder: "Promemoria",
    }[kind];
    return (
      <div className="pxos-card p-3 border-2" style={{ borderColor: "var(--pxos-accent)" }}
           data-testid={`chatbot-action-card-${kind}`} data-action-status={status}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-1.5">
          <span>{isExecuted ? "✓ Confermato" : isRejected ? "✕ Annullato" : "Azione proposta"}</span>
          <span>·</span>
          <span>{kindLabel}</span>
        </div>
        <div className="text-xs space-y-1">
          {kind === "add_memory" && <>
            {p.category && <div>Categoria: <b>{p.category}</b></div>}
            <div>Testo: <i>{(p.content || "").slice(0, 200)}</i></div>
            {p.pinned && <div>★ Fissata in cima</div>}
          </>}
          {kind === "create_reminder" && <>
            {p.title && <div>Titolo: <b>{p.title}</b></div>}
            {p.due_date && <div>Scadenza: <b>{fmtDate(p.due_date)}</b></div>}
            {p.offsets_days && <div>Reminder: <b>{p.offsets_days.join(", ")} giorni prima</b></div>}
          </>}
          {kind === "link_document_finance" && <>
            <div>Documento: <code>{(p.document_id || "").slice(0, 8)}…</code></div>
            <div>Voce: <code>{(p.financial_item_id || "").slice(0, 8)}…</code></div>
            <div>Ruolo: <b>{p.role || "attached"}</b></div>
          </>}
        </div>
        {isProposed && (
          <div className="flex items-center gap-2 mt-3">
            <button onClick={handleConfirmClick}
                    className="pxos-btn-primary text-xs" data-testid="chatbot-action-confirm">
              <Check size={12}/> Conferma
            </button>
            <button onClick={() => onReject(action.id)}
                    className="text-xs underline opacity-70" data-testid="chatbot-action-reject">
              Annulla
            </button>
          </div>
        )}
      </div>
    );
  }

  // Default: cycle / goal
  const type = p.type || "task";
  const typeLabel = {
    task: "Ciclo", deadline: "Scadenza", payment_reminder: "Pagamento",
    urgency: "Urgenza", event: "Evento", call: "Call", meeting: "Meeting",
    appointment: "Appuntamento", followup: "Follow-up", note: "Nota", habit: "Abitudine",
  }[type] || type;

  return (
    <div className="pxos-card p-3 border-2" style={{ borderColor: "var(--pxos-accent)" }} data-testid="chatbot-action-card" data-action-status={status}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-1.5">
        <span>{isExecuted ? "✓ Confermato" : isRejected ? "✕ Annullato" : "Azione proposta"}</span>
        <span>·</span>
        <span>{typeLabel}</span>
      </div>
      <div className="text-sm font-medium mb-2">{p.title || "(senza titolo)"}</div>
      <div className="text-xs opacity-80 space-y-0.5">
        {p.due_date && <div>Scadenza: <b>{fmtDate(p.due_date)}</b>{p.due_time ? ` · ${p.due_time}` : ""}</div>}
        {p.scheduled_date && <div>Programmata: <b>{fmtDate(p.scheduled_date)}</b>{p.scheduled_time ? ` · ${p.scheduled_time}` : ""}</div>}
        {p.priority && p.priority !== "medium" && <div>Priorità: <b>{p.priority}</b></div>}
        {p.life_area_name && <div>Area: <b>{p.life_area_name}</b></div>}
        {p.reminder_offsets_days?.length > 0 && <div>Reminder: <b>{p.reminder_offsets_days.join(", ")} giorni prima</b></div>}
        {p.recurrence && p.recurrence !== "none" && <div>Ricorrenza: <b>{p.recurrence}</b></div>}
      </div>
      {isProposed && (
        <div className="flex items-center gap-2 mt-3">
          <Tip label="Conferma e crea il ciclo">
            <button onClick={handleConfirmClick}
                    className="pxos-btn-primary text-xs" data-testid="chatbot-action-confirm">
              <Check size={12}/> Conferma
            </button>
          </Tip>
          <Tip label="Non creare, ignora questa proposta">
            <button onClick={() => onReject(action.id)}
                    className="text-xs underline opacity-70" data-testid="chatbot-action-reject">
              Annulla
            </button>
          </Tip>
        </div>
      )}
      {isExecuted && (
        <div className="text-xs opacity-70 mt-2 flex items-center gap-1">
          <Check size={12} className="text-green-700"/> Ciclo creato · <span className="opacity-70">annulla nel toast per 60s</span>
        </div>
      )}
    </div>
  );
}

function InlineStepupPrompt({ pwd, setPwd, showPwd, setShowPwd, pwdBusy, submitStepup, onCancel }) {
  return (
    <div className="mt-3 pxos-card p-2.5" style={{ background: "#FFF8E1", borderColor: "#F59E0B" }}
         data-testid="chatbot-stepup-prompt">
      <div className="flex items-center gap-1.5 text-[11px] font-medium mb-2" style={{ color: "#8A5A00" }}>
        <Lock size={12}/> Inserisci la tua password per confermare
      </div>
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <input type={showPwd ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") submitStepup(); }}
                 autoFocus disabled={pwdBusy}
                 className="pxos-input w-full text-xs pr-8"
                 placeholder="Password" data-testid="chatbot-stepup-password"/>
          <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
                  aria-label={showPwd ? "Nascondi password" : "Mostra password"}>
            {showPwd ? <EyeOff size={14}/> : <Eye size={14}/>}
          </button>
        </div>
        <button onClick={submitStepup} disabled={pwdBusy || !pwd}
                className="pxos-btn-primary text-xs px-2 py-1"
                data-testid="chatbot-stepup-submit">
          {pwdBusy ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
        </button>
        <button onClick={onCancel} disabled={pwdBusy}
                className="text-xs underline opacity-70"
                data-testid="chatbot-stepup-cancel">
          Annulla
        </button>
      </div>
      <div className="text-[10px] opacity-70 mt-1.5">
        Token valido 15 min · ~5 tentativi
      </div>
    </div>
  );
}

function fmtEur(n) {
  if (n == null) return "—";
  try { return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n)); }
  catch { return `€ ${n}`; }
}

const FREQ_IT_LABELS = {
  weekly: "Settimanale", monthly: "Mensile", bimonthly: "Bimestrale",
  quarterly: "Trimestrale", semiannual: "Semestrale", yearly: "Annuale",
  custom: "Personalizzata",
};

function fmtDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT"); }
  catch { return iso; }
}

/**
 * Normalize FastAPI/axios error to a safe string for JSX rendering.
 * FastAPI ValidationError returns `detail` as an array of {loc,msg,type,...}
 * which would crash React ("Objects are not valid as a React child").
 */
function errMsg(e, fallback = "Errore") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === "string" ? x : x?.msg || JSON.stringify(x))).join(" · ");
  }
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  if (typeof e?.message === "string") return e.message;
  return fallback;
}
